import { newsItems } from "@/mocks/home";
import SectionHeading from "./SectionHeading";

interface NewsProps {
  tag: string;
  onTag: (t: string) => void;
  query: string;
  onClearQuery: () => void;
}

const tabs = ["全部", ...Array.from(new Set(newsItems.map((n) => n.tag)))];

const tagColor: Record<string, string> = {
  "影视剧综": "bg-primary-100 text-primary-700",
  "互联网观察": "bg-accent-100 text-accent-700",
  "体育电竞": "bg-secondary-100 text-secondary-800",
  "生活热梗": "bg-background-200 text-foreground-700",
};

export default function News({ tag, onTag, query, onClearQuery }: NewsProps) {
  const kw = query.trim().toLowerCase();
  const filtered = newsItems.filter((n) => {
    const matchTag = tag === "全部" || n.tag === tag;
    const matchKw = !kw || n.title.toLowerCase().includes(kw);
    return matchTag && matchKw;
  });

  return (
    <section id="news" className="bg-background-100 py-12 md:py-16">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeading
            kicker="最新动态 · 实时更新"
            title="此刻大家正在聊什么"
            desc="按板块与关键词筛选最新讨论，找到正在发酵的瓜，别错过前排队友的第一手观点。"
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div
            className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1"
            role="tablist"
            aria-label="按板块筛选"
          >
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tag === t}
                onClick={() => onTag(t)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  tag === t ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {kw && (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-800">
              <i className="ri-search-line" aria-hidden="true"></i>
              正在搜索：“{query}”
              <button
                type="button"
                onClick={onClearQuery}
                aria-label="清除搜索"
                className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-secondary-200 text-secondary-800"
              >
                <i className="ri-close-line text-xs" aria-hidden="true"></i>
              </button>
            </span>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {filtered.length > 0 ? (
              <ol className="space-y-3">
                {filtered.map((n) => (
                  <li key={n.title}>
                    <a
                      href="#reviews"
                      className="flex items-start justify-between gap-4 rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 hover:bg-background-50 md:p-5"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground-950 hover:text-primary-600 md:text-base">
                          {n.title}
                        </span>
                        <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-600">
                          <span className={`rounded px-1.5 py-0.5 ${tagColor[n.tag] ?? "bg-background-200 text-foreground-700"}`}>
                            {n.tag}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <i className="ri-time-line" aria-hidden="true"></i>
                            {n.time}
                          </span>
                        </span>
                      </span>
                      <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-700">
                        <i className="ri-chat-3-line" aria-hidden="true"></i>
                        {n.replies} 讨论
                      </span>
                    </a>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <i className="ri-inbox-line text-4xl text-foreground-300" aria-hidden="true"></i>
                <p className="mt-3 text-sm text-foreground-700">没有找到相关讨论</p>
                <p className="mt-1 text-xs text-foreground-600">换个关键词，或清除搜索看看全部内容。</p>
                <button
                  type="button"
                  onClick={() => {
                    onClearQuery();
                    onTag("全部");
                  }}
                  className="mt-4 inline-flex items-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600"
                >
                  清除筛选
                </button>
              </div>
            )}
          </div>

          <aside className="rounded-2xl border border-background-200 bg-background-50 p-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
              <i className="ri-quill-pen-line text-primary-500" aria-hidden="true"></i>
              想上热评？先懂这几个门道
            </h3>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-700">
              <li className="flex items-start gap-2">
                <i className="ri-number-1 mt-0.5 text-primary-500" aria-hidden="true"></i>
                观点清晰，先亮立场再说理由，别绕圈子。
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-number-2 mt-0.5 text-accent-600" aria-hidden="true"></i>
                摆事实讲依据，用证据支撑而不是情绪刷屏。
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-number-3 mt-0.5 text-secondary-600" aria-hidden="true"></i>
                愿意补一句“我理解另一边的看法”，更有含金量。
              </li>
            </ul>
            <a
              href="#about"
              className="mt-5 inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-secondary-100 px-4 py-2.5 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              查看完整讨论指南
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
}